/** 根据真实购物车写入和 App 结账准备状态控制按钮，不通过固定时间强制放行。 */
const CartCheckoutWaitGuard = {
  initialized: false,
  appWriting: false,
  checkoutWriting: false,
  codeChecks: 0,

  /** 注册一次状态事件及分区重建监听，无参数。 */
  init() {
    if (this.initialized) return;
    this.initialized = true;
    document.addEventListener('vo:cart-writing', event => {
      if (event.detail?.source !== 'theme-cart') this.appWriting = true;
      this.requestSync();
    });
    document.addEventListener('vo:cart-changed', () => { this.appWriting = false; CartQuantityQueue.resume(); this.requestSync(); });
    this.checkoutWriting = ['pending', 'redirecting'].includes(document.querySelector('[data-vo-cart-pricing]')?.dataset.checkoutState);
    document.addEventListener('vo:checkout-state', event => {
      this.checkoutWriting = event.detail?.active === true;
      CartQuantityQueue.resume();
      this.requestSync();
    });
    this.observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node instanceof Element
        && (node.matches('[data-cart-checkout-button]') || node.querySelector('[data-cart-checkout-button]'))))) this.requestSync();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.requestSync();
  },

  /** 按当前待提交意图、网络请求及 App 结账状态同步按钮，无参数。 */
  requestSync() {
    const waiting = CartQuantityQueue.running || CartQuantityQueue.jobs.length > 0 || this.appWriting || this.checkoutWriting || this.codeChecks > 0;
    // 使用容器 inert 避免与 App 自己管理的 disabled 状态互相覆盖。
    document.querySelectorAll('cart-code-modal, .cart-coupon-button, .cart-note-button, cart-note-modal').forEach(node => { node.inert = waiting; });
    document.querySelectorAll('cart-items .cart-items-list, gift-wrapping').forEach(node => {
      node.inert = waiting;
    });
    document.querySelectorAll('[data-cart-checkout-button]').forEach(button => {
      button.disabled = waiting;
      button.classList.toggle('loading', waiting);
      button.setAttribute('aria-disabled', String(waiting));
      button.setAttribute('aria-busy', String(waiting));
    });
    document.querySelectorAll('.main-cart-content .cart-details-footer').forEach(footer => {
      footer.classList.toggle('is-footer-ready', !waiting);
    });
  },
};

/** 整个页面共享的 Cart 写入队列，跨商品、跨购物车分区保持串行，不恢复旧行号记录。 */
const CartQuantityQueue = {
  jobs: [],
  pending: new Map(),
  running: false,
  activeJob: null,
  timer: null,
  signaled: false,
  writeId: null,
  requestSequence: 0,

  /**
   * 读取 Liquid 行属性，只还原键值对数组中一层展示编码；原生 Cart 对象保持原值。
   * @param {string} serialized data-line-properties 中已由浏览器解析属性边界的 JSON 字符串。
   * @returns {object|Array} 与 Ajax Cart 一致的属性对象或键值对数组。
   */
  propertiesFromMarkup(serialized) {
    const properties = JSON.parse(serialized);
    if (!Array.isArray(properties)) return properties;
    const decoder = document.createElement('textarea');
    /** @param {string} value Liquid 编码后的属性名或选值。@returns {string} 只还原一层实体的原文。 */
    const decode = value => typeof value === 'string' ? value.replace(/&(?:#[0-9]+|#x[0-9a-f]+|[a-z][a-z0-9]+);/gi, entity => {
      // 只让浏览器解析单个实体，不把完整定制文本作为 HTML 插入，且不递归解码字面实体。
      decoder.innerHTML = entity;
      return decoder.textContent;
    }) : value;
    return properties.map(([key, value]) => [decode(key), decode(value)]);
  },

  /**
   * 根据全部定制属性和购买方案识别商品，数量变化不改变目标身份。
   * @param {string|number} variantId 商品 Variant ID。
   * @param {object|Array|null} properties Ajax Cart 属性对象或 Liquid 输出的键值对数组。
   * @param {string|number|null} sellingPlan 当前购买方案 ID。
   * @returns {string} 包含全部属性的规范身份。
   */
  identity(variantId, properties, sellingPlan) {
    // Liquid 将 properties 序列化为键值对数组，Ajax Cart 则返回对象；先统一格式再校验归属。
    const pairs = Array.isArray(properties) ? properties : Object.entries(properties || {});
    const entries = [...pairs]
      .sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0);
    return JSON.stringify([String(variantId), String(sellingPlan || ''), entries]);
  },

  /**
   * 合并尚未发送的同一定制行操作，所有输入只使用这一层防抖。
   * @param {object} operation 行 key、完整身份、目标数量及来源组件。
   */
  enqueue(operation) {
    const queueKey = operation.identity + '|' + operation.key;
    const previous = this.pending.get(queueKey);
    // inert 触发失焦时可能再次产生相同 change；不能把同一在途意图当成失败后的自动重试。
    if (!previous && this.activeJob?.kind === 'quantity' && this.activeJob.queueKey === queueKey
      && this.activeJob.quantity === operation.quantity) return;
    if (previous) Object.assign(previous, operation);
    else {
      const job = { ...operation, queueKey, kind: 'quantity' };
      this.pending.set(queueKey, job);
      this.jobs.push(job);
    }
    this.schedule(operation.quantity === 0 ? 0 : 250);
  },

  /**
   * 将备注和包装写入纳入同一队列，防止它们与数量更新交错。
   * @param {Function} execute 包含网络响应解析和分区更新的完整异步操作。
   * @returns {Promise<unknown>} 当前操作的结果。
   */
  transaction(execute) {
    return new Promise((resolve, reject) => {
      this.jobs.push({ kind: 'external', execute, resolve, reject });
      this.schedule(0);
    });
  },

  /**
   * 安排一次共享队列处理，开始等待即通知报价器废弃旧金额。
   * @param {number} delay 唯一一层防抖时间，单位毫秒。
   */
  schedule(delay) {
    if (!this.signaled) {
      this.signaled = true;
      document.dispatchEvent(new CustomEvent('vo:cart-writing', { detail: { source: 'theme-cart' } }));
    }
    clearTimeout(this.timer);
    if (!this.running) this.timer = setTimeout(() => this.process(), delay);
    CartCheckoutWaitGuard.requestSync();
  },

  /** App 写入或折扣预检结束后，继续处理等待中的原生数量操作，无参数。 */
  resume() {
    if (!this.running && this.jobs.length && !CartCheckoutWaitGuard.appWriting
      && !CartCheckoutWaitGuard.checkoutWriting && !CartCheckoutWaitGuard.codeChecks) this.schedule(0);
  },

  /**
   * 请求本店 Cart API；失败不自动重放写入，避免网络中断后重复变更。
   * @param {string} url 当前店铺的 Cart API 路径。
   * @param {object|null} body POST 数据；null 表示只读 GET。
   * @returns {Promise<object>} 已确认成功的原生响应。
   */
  async http(url, body = null) {
    const target = new URL(url, window.location.href);
    if (target.origin !== window.location.origin) throw new Error(window.cartStrings.error);
    if (body !== null) {
      // 标记确切的原生写请求；App 忽略其迟到网络通知，只在整车队列结束后重新报价。
      this.writeId = `vo-theme-${Date.now()}-${++this.requestSequence}`;
      target.searchParams.set('vo_quote_write', this.writeId);
      document.dispatchEvent(new CustomEvent('vo:cart-writing', { detail: { source: 'theme-cart', id: this.writeId } }));
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), body === null ? 10000 : 20000);
    try {
      const response = await fetch(target, { method: body === null ? 'GET' : 'POST', credentials: 'same-origin',
        cache: 'no-store', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...(body === null ? {} : { body: JSON.stringify(body) }), signal: controller.signal });
      const data = await response.json();
      if (!response.ok || data.errors || data.error || !Array.isArray(data.items)) throw new Error(window.cartStrings.error);
      return data;
    } finally { clearTimeout(timeout); }
  },

  /** 读取当前店铺的最新本地化购物车，无参数。 */
  readCart() {
    return this.http(window.routes.cart_url.replace(/\/$/, '') + '.js');
  },

  /** 获取页面和抽屉实际存在的分区，避免一边更新后另一边保留旧数量，无参数。 */
  sections() {
    const unique = new Map();
    document.querySelectorAll('cart-items').forEach(owner => owner.getSectionsToRender().forEach(section => {
      unique.set(section.id + ':' + section.selector, section);
    }));
    return [...unique.values()];
  },

  /**
   * 核对目标行的完整身份，并使用最新行 key 写入数量。
   * @param {object} job 已合并的最终数量意图。
   * @returns {Promise<object|null>} 实际写入响应；无需变更时返回 null。
   */
  async change(job) {
    const cart = await this.readCart();
    const matches = cart.items.filter(item => this.identity(item.variant_id, item.properties,
      item.selling_plan_allocation?.selling_plan?.id) === job.identity);
    // 禁止按行号或 Variant 猜测归属；无法唯一确认时拒绝这次操作。
    const exact = matches.filter(item => item.key === job.key);
    const item = exact.length === 1 ? exact[0] : matches.length === 1 ? matches[0] : null;
    if (!item) throw new Error(window.cartStrings.error);
    if (item.quantity === job.quantity) return null;
    const sections = this.sections();
    const sectionRequest = { sections: [...new Set(sections.map(section => section.section))], sections_url: window.location.pathname };
    let result = await this.http(window.routes.cart_change_url, { id: item.key, quantity: job.quantity, ...sectionRequest });
    const updated = result.items.find(row => row.key === item.key
      || this.identity(row.variant_id, row.properties, row.selling_plan_allocation?.selling_plan?.id) === job.identity);
    if (job.quantity > 0 && (!updated || updated.quantity !== job.quantity)) {
      throw new Error(window.cartStrings.quantityError.replace('[quantity]', updated?.quantity ?? item.quantity));
    }
    // 包装仍按原生非包装商品数量同步；在当前串行任务内完成，避免递归排队和旧行号。
    const giftId = job.owner.dataset.giftWrapId || document.querySelector('gift-wrapping')?.dataset.giftWrappingId;
    const gifts = result.items.filter(row => String(row.variant_id) === String(giftId || ''));
    if (gifts.length === 1) {
      const gift = gifts[0];
      const quantity = result.items.filter(row => row !== gift).reduce((sum, row) => sum + row.quantity, 0);
      if (quantity !== gift.quantity) result = await this.http(window.routes.cart_update_url, {
        updates: { [gift.key]: quantity }, ...(quantity ? {} : { attributes: { 'gift-wrap': '' } }), ...sectionRequest,
      });
    }
    return { cart: result, sections, owner: job.owner, variantId: job.variantId, focus: job.focus };
  },

  /**
   * 同步当前仍在 DOM 中的组件状态，不持有已移除组件的全局监听。
   * @param {boolean} loading 是否正在写入。
   */
  setLoading(loading) {
    document.querySelectorAll('cart-items').forEach(owner => loading ? owner.enableLoading() : owner.disableLoading());
  },

  /**
   * 分区被其他操作重建时，仅恢复当前页面的待提交显示，不再次排队或写入。
   * @param {HTMLElement} owner 当前已连接的购物车组件。
   */
  paintPending(owner) {
    owner.querySelectorAll('.quantity-input[data-index]').forEach(input => {
      try {
        const target = owner.target(Number(input.dataset.index));
        const jobs = [...this.pending.values()];
        if (!jobs.some(job => job.identity === target.identity) && this.activeJob?.kind === 'quantity') jobs.push(this.activeJob);
        const exact = jobs.filter(job => job.key === target.key && job.identity === target.identity);
        const matching = exact.length ? exact : jobs.filter(job => job.identity === target.identity);
        if (matching.length === 1) input.value = matching[0].quantity;
      } catch { /* 无法确认的旧节点保持服务端返回值，不猜测归属。 */ }
    });
  },

  /**
   * 应用当前串行队列中最新的整车响应。
   * @param {object} result 已确认的响应及对应分区、焦点信息。
   */
  draw(result) { result.owner.handleCartUpdate(result.cart, result.variantId, result.focus, result.sections); },

  /** 串行执行所有原生写入，最终整车响应仅重绘一次；失败后允许用户重试，无参数。 */
  async process() {
    if (this.running || CartCheckoutWaitGuard.appWriting || CartCheckoutWaitGuard.checkoutWriting || CartCheckoutWaitGuard.codeChecks) return;
    this.running = true;
    this.writeId = null;
    clearTimeout(this.timer);
    this.setLoading(true);
    CartCheckoutWaitGuard.requestSync();
    let latest = null;
    try {
      while (this.jobs.length) {
        const job = this.jobs.shift();
        this.activeJob = job;
        if (job.kind === 'quantity') this.pending.delete(job.queueKey);
        try {
          if (job.kind === 'quantity') {
            const result = await this.change(job);
            if (result) latest = result;
          } else {
            // 先提交之前成功的数量结果；外部任务失败时也不能丢失已完成的数量更新。
            if (latest) this.draw(latest);
            latest = null;
            job.resolve(await job.execute());
          }
        } catch (error) {
          if (job.kind === 'external') job.reject(error);
          else {
            job.owner.showError(error.message || window.cartStrings.error);
            job.owner.restoreLineItemQuantityInputs(job.line);
          }
        }
        this.activeJob = null;
      }
      if (latest) this.draw(latest);
    } catch {
      webvista.popToast(window.cartStrings.error, 'error');
    } finally {
      this.running = false;
      this.activeJob = null;
      this.setLoading(false);
      this.signaled = false;
      const writeId = this.writeId;
      this.writeId = null;
      document.dispatchEvent(new CustomEvent('vo:cart-changed', { detail: { writeId } }));
      CartCheckoutWaitGuard.requestSync();
      if (this.jobs.length) this.schedule(0);
    }
  },
};

if (!customElements.get('cart-items')) {
  /** 原生购物车数量交互；金额与折扣仍由 Shopify 和 App 主题扩展处理。 */
  class CartItems extends HTMLElement {
    /** 仅保存本组件的事件引用，子节点在 connectedCallback 中读取，无参数。 */
    constructor() {
      super();
      this.boundChange = this.onChange.bind(this);
    }

    /** 连接时绑定一次事件；分区重建不再重放 localStorage 的旧行号，无参数。 */
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.inDrawer = this.hasAttribute('data-in-drawer');
      this.drawerTrap = this.inDrawer ? this.closest('[data-trap]') : null;
      this.cartItemsContainer = this.querySelector('.cart-items-list');
      this.lineItemStatusElement = document.getElementById(`Cart-Line-Item-Status-${this.sectionId}`);
      this.removeEventListener('change', this.boundChange);
      this.addEventListener('change', this.boundChange);
      CartQuantityQueue.paintPending(this);
      if (!CartQuantityQueue.running && this.hasAttribute('data-gift-wrap-id')
        && Number(this.dataset.giftWrapQuantity) !== Number(this.dataset.itemQuantity)) {
        this.updateQuantity(this.dataset.giftWrapIndex, this.dataset.itemQuantity, null, this.dataset.giftWrapId);
      }
    }

    /** 移除本组件的事件；共享队列继续处理已经确认的用户意图，无参数。 */
    disconnectedCallback() { this.removeEventListener('change', this.boundChange); }

    /**
     * 只处理数量输入，其他 VO 表单字段不能误触数量更新。
     * @param {Event} event 当前输入控件的 change 事件。
     */
    onChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('.quantity-input[data-index]')) return;
      this.updateQuantity(input.dataset.index, input.value, input, input.dataset.quantityVariantId);
    }

    /**
     * 获取本分区中的商品行节点。
     * @param {string|number} line 仅用于查找当前 DOM 的一基行号。
     * @returns {Element|null} 当前行节点。
     */
    getCartItemElement(line) { return this.querySelector(`#Cart-Item-${this.sectionId}-${Number(line)}`); }

    /**
     * 从 Liquid 输出中读取真实行身份，不从标题或金额推断。
     * @param {number} line 当前 DOM 的行号。
     * @returns {object} 行 key、Variant、全部属性和购买方案。
     */
    target(line) {
      const node = this.querySelector(`cart-remove[data-index="${line}"]`) || this.getCartItemElement(line);
      if (!node?.dataset.lineKey || !node.dataset.lineVariantId) throw new Error(window.cartStrings.error);
      const properties = CartQuantityQueue.propertiesFromMarkup(node.dataset.lineProperties || '{}');
      return { key: node.dataset.lineKey, variantId: node.dataset.lineVariantId,
        identity: CartQuantityQueue.identity(node.dataset.lineVariantId, properties, node.dataset.lineSellingPlan) };
    }

    /**
     * 将目标数量交给整车队列，立即更新输入显示但不伪造金额。
     * @param {string|number} line 当前 DOM 行号。
     * @param {string|number} quantity 用户最终希望的数量，0 表示删除。
     * @param {Element|null} focusableElement 操作完成后需要恢复的焦点控件。
     * @param {string|number|null} variantId 原生调用方提供的 Variant，仅用于一致性校验。
     */
    updateQuantity(line, quantity, focusableElement = null, variantId = null) {
      line = Number(line);
      quantity = Number(quantity);
      if (!Number.isSafeInteger(line) || line < 1) return;
      if (!Number.isSafeInteger(quantity) || quantity < 0) {
        this.restoreLineItemQuantityInputs(line);
        this.showError(window.cartStrings.error);
        return;
      }
      try {
        const target = this.target(line);
        if (variantId && String(variantId) !== target.variantId) throw new Error(window.cartStrings.error);
        this.querySelectorAll(`.quantity-input[data-index="${line}"]`).forEach(input => { input.value = quantity; });
        CartQuantityQueue.enqueue({ ...target, line, quantity, focus: focusableElement, owner: this });
      } catch { this.showError(window.cartStrings.error); this.restoreLineItemQuantityInputs(line); }
    }

    /**
     * 使用已确认的最终响应更新分区并广播，重建组件不再触发自动数量请求。
     * @param {object} responseJson Shopify 最终 Cart 响应。
     * @param {string|number|null} variantId 本次操作的 Variant。
     * @param {Element|null} focusableElement 操作前的焦点控件。
     * @param {object[]} sections 本次请求实际返回的分区列表。
     */
    handleCartUpdate(responseJson, variantId, focusableElement, sections = this.getSectionsToRender()) {
      SectionDynamicUpdate.updateSections(sections, responseJson.sections);
      if (this.drawerTrap) webvista.trapFocus(this.drawerTrap, focusableElement?.isConnected ? focusableElement : null);
      else if (focusableElement?.isConnected) focusableElement.focus();
      webvista.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: variantId, cartData: responseJson });
    }

    /**
     * 请求失败时恢复本分区仍存在的数量控件。
     * @param {number} line 当前 DOM 行号。
     */
    restoreLineItemQuantityInputs(line) {
      this.querySelectorAll(`.quantity-input[data-index="${Number(line)}"]`).forEach(input => { input.value = input.getAttribute('value'); });
    }

    /** 返回原生页面或抽屉的分区定义，无参数。 */
    getSectionsToRender() {
      return [{ id: 'Cart-Icon-Bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        this.inDrawer ? { id: 'Cart-Drawer', section: this.sectionId, selector: '#Cart-Drawer-Details' }
          : { id: 'Main-Cart', section: this.sectionId, selector: '#Main-Cart-Details' }];
    }

    /**
     * 沿用原生错误提示。
     * @param {string|null} message 当前用户可见错误。
     */
    showError(message = null) { if (message) webvista.popToast(message, 'error'); }

    /** 启用原生数量写入 Loading，无参数。 */
    enableLoading() {
      this.cartItemsContainer?.classList.add('cart-items--disabled');
      this.lineItemStatusElement?.setAttribute('aria-hidden', 'false');
    }

    /** 所有整车写入结束后解除原生 Loading，无参数。 */
    disableLoading() {
      if (CartQuantityQueue.running) return;
      this.cartItemsContainer?.classList.remove('cart-items--disabled');
      this.lineItemStatusElement?.setAttribute('aria-hidden', 'true');
    }
  }
  customElements.define('cart-items', CartItems);
}

/**
 * 删除购物车 Item
 */
if (!customElements.get("cart-remove")) {
  customElements.define(
    "cart-remove",
    class CartRemove extends HTMLElement {
      constructor() {
        super();

        this.addEventListener("click", (event) => {
          event.preventDefault();

          const cartItems = this.closest("cart-items");
          if (cartItems) cartItems.updateQuantity(this.dataset.index, 0);
        });
      }
    },
  );
}


if (!customElements.get("free-shipping-progress")) {
  customElements.define(
    "free-shipping-progress",
    class FreeShippingProgress extends HTMLElement {
      /**
       * data-total-amount 购物车总价金额，单位分
       * data-free-threshold 包邮门槛金额，单位元
       * data-confetti 触发彩带庆祝效果
       */
      constructor() {
        super();

        this.renderProgress();
      }

      get progressBar() {
        return this.querySelector(".free-shipping-progress");
      }

      get messageWrapper() {
        return this.querySelector(".free-shipping-message");
      }

      /**
       * 渲染包邮进度条
       */
      renderProgress() {
        const cartTotalAmount = this.dataset.totalAmount;
        const freeThreshold = this.dataset.freeThreshold;

        if (
          !webvista.isNumeric(cartTotalAmount) ||
          !webvista.isNumeric(freeThreshold)
        )
          return;

        const freeThresholdLocal =
          Math.round(parseInt(freeThreshold) * (Shopify.currency.rate || 1)) *
          100; // 门槛金额汇率转换，单位分

        let progress;
        if (cartTotalAmount - freeThresholdLocal >= 0) {
          progress = 100;
          this.messageWrapper.innerHTML =
            window["cartStrings"]["freeShippingUnlockedMessage"];
          this.classList.add("free-has-unlocked");
        } else {
          progress = (cartTotalAmount * 100) / freeThresholdLocal;

          const amountToQualify = freeThresholdLocal - cartTotalAmount;
          this.messageWrapper.innerHTML = window["cartStrings"][
            "freeShippingLockedMessage"
          ].replace(
            "[amount]",
            webvista.formatPriceAmount(
              amountToQualify,
              Shopify.currency.active,
            ),
          );
        }

        // 进度条进度
        if (this.progressBar)
          this.progressBar.style.setProperty(
            "--free-shipping-progress",
            `${progress}%`,
          );

        // 触发庆祝彩带特效
        if (this.hasAttribute("data-confetti")) {
          document.dispatchEvent(
            new CustomEvent("freeShippingUnlocked", {
              detail: { status: progress >= 100 },
            }),
          );
        }
      }
    },
  );
}

/**
 * 用于购物车备注弹窗的自定义元素
 */
if (!customElements.get("cart-note-modal")) {
  customElements.define(
    "cart-note-modal",
    class CartNoteModal extends ModalDialog {
      constructor() {
        super();

        const form = this.querySelector("form");
        if (!form) return;

        this.submitButton = this.querySelector("button[type=submit]");
        form.addEventListener("submit", this.formSubmit.bind(this));
      }

      /**
       * 通过整车队列保存备注，并只在确认成功后关闭表单。
       * @param {SubmitEvent} event 当前备注表单提交事件。
       * @returns {Promise<void>} 保存成功或显示错误后恢复按钮。
       */
      async formSubmit(event) {
        event.preventDefault();
        this.startLoading();
        const note = new FormData(event.target).get('note');
        try {
          await CartQuantityQueue.transaction(async () => {
            const cart = await CartQuantityQueue.http(window.routes.cart_update_url, { note: String(note || '') });
            // 两处备注使用同一保存结果；等待期间由守卫锁定，不覆盖正在编辑的其他表单。
            document.querySelectorAll('cart-note-modal textarea[name="note"]').forEach(input => {
              input.value = cart.note || '';
              input.defaultValue = input.value;
            });
          });
          webvista.popToast(window.cartStrings.addToNoteSuccess, 'success');
          this.hide();
        } catch {
          webvista.popToast(window.cartStrings.addToNoteError, 'error');
        } finally {
          this.endLoading();
        }
      }

      startLoading() {
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }
    },
  );
}

/**
 * 订单运费预估
 */
if (!customElements.get("shipping-calculator-modal")) {
  customElements.define(
    "shipping-calculator-modal",
    class ShippingCalculatorModal extends ModalDialog {
      static MAX_ATTEMPTS = 3;
      constructor() {
        super();

        this.asyncAttemptCount = 0; // Async请求尝试次数

        const form = this.querySelector("form");
        if (!form) return;

        form.addEventListener("submit", this.prepareRate.bind(this));

        this.submitButton = this.querySelector("button[type=submit]");
        this.successMessageElement = this.querySelector(".alert-success");
      }

      /**
       * 请求查询物流信息
       * @param event
       */
      prepareRate(event) {
        event.preventDefault();

        this.hideResults();
        this.startLoading();

        const formData = new FormData(event.target);
        const shipping_address = {
          country: formData.get("country"),
          province: formData.get("province"),
          zip: formData.get("zip"),
        };
        const body = JSON.stringify({ shipping_address: shipping_address });

        fetch(`${window["routes"]["cart_url"]}/prepare_shipping_rates.json`, {
          ...webvista.fetchConfig(),
          ...{ body },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Network response was not ok: ${response.statusText}`,
              );
            }
            return response.json();
          })
          .then((response) => {
            if (response == null || response.ok) {
              this.asyncRate({ shipping_address: shipping_address });
            } else {
              throw new Error("Prepare shipping rates error");
            }
          })
          .catch((error) => {
            this.showError();
          });
      }

      /**
       * 异步查询返回的信息
       * @param params
       */
      asyncRate(params) {
        fetch(
          `${window["routes"]["cart_url"]}/async_shipping_rates.json?` +
          this.serialize(params),
          { ...webvista.fetchConfig("json", "GET") },
        )
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (
              response != null &&
              response["shipping_rates"] != null &&
              response["shipping_rates"].length > 0
            ) {
              this.showResults(response);
            } else {
              if (
                ++this.asyncAttemptCount >= ShippingCalculatorModal.MAX_ATTEMPTS
              ) {
                this.showError();
              } else {
                setTimeout(() => this.asyncRate(params), 3000);
              }
            }
          })
          .catch((error) => {
            this.showError();
          });
      }

      startLoading() {
        this.submitButton.classList.add("loading");
        this.submitButton.setAttribute("disabled", "disabled");
      }

      endLoading() {
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("disabled");
      }

      showError() {
        this.endLoading();
        webvista.popToast(
          window["cartStrings"]["estimateShippingError"],
          "error",
        );
      }

      /**
       * 显示查询到的结果
       * @param result
       */
      showResults(result) {
        this.endLoading();

        const rates = result["shipping_rates"];
        if (!rates || rates.length <= 0 || !this.successMessageElement) return;

        // 标题修改
        const countElement =
          this.successMessageElement.querySelector(".result-count");
        countElement.innerText = rates.length;

        // 显示查询到的物流列表
        const template =
          this.successMessageElement.querySelector("template").content; // 消息模板
        const fragment = document.createDocumentFragment();
        rates.forEach((rate) => {
          const clone = document.importNode(template, true);

          clone.querySelector(".presentment-name").textContent =
            rate["presentment-name"] || rate["name"];
          clone.querySelector(".delivery-price").textContent = window[
            "priceFormatTemplate"
          ].replace(/0([,.]0{0,2})?/, rate["price"]);

          let deliveryDate = rate["delivery_date"];
          if (rate["delivery_range"] && Array.isArray(rate["delivery_range"])) {
            if (rate["delivery_range"].length > 1) {
              if (rate["delivery_range"][0] === rate["delivery_range"][1]) {
                deliveryDate = rate["delivery_range"][0];
              } else {
                deliveryDate = rate["delivery_range"].join(" - ");
              }
            } else {
              deliveryDate = rate["delivery_range"][0];
            }
          }

          if (!deliveryDate || deliveryDate.trim() === "") {
            clone.querySelector(".delivery-date-wrapper")?.remove();
          } else {
            clone.querySelector(".delivery-date").textContent = deliveryDate;
          }

          fragment.appendChild(clone);
        });

        this.successMessageElement
          .querySelector(".message-list")
          ?.appendChild(fragment);
        this.successMessageElement.removeAttribute("hidden");
        this.endLoading();
      }

      hideResults() {
        if (this.successMessageElement) {
          this.successMessageElement.querySelector(".message-list").innerHTML =
            "";
          this.successMessageElement.setAttribute("hidden", "true");
        }
      }

      serialize(obj, prefix) {
        const str = Object.keys(obj)
          .filter((key) => obj.hasOwnProperty(key))
          .map((key) => {
            const k = prefix ? `${prefix}[${key}]` : key;
            const v = obj[key];
            return v !== null && typeof v === "object"
              ? this.serialize(v, k)
              : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          });
        return str.join("&");
      }
    },
  );
}

/**
 * 礼品包装
 */
if (!customElements.get("gift-wrapping")) {
  customElements.define(
    "gift-wrapping",
    class GiftWrapping extends HTMLElement {
      /** 保存本组件事件引用，子节点在连接后读取，无参数。 */
      constructor() { super(); this.boundChange = this.onChange.bind(this); }

      /** 按当前分区初始化包装控件，不使用组件外的防抖定时器，无参数。 */
      connectedCallback() {
        this.giftWrapId = this.dataset.giftWrappingId;
        this.itemQuantity = Math.max(0, parseInt(this.dataset.itemQuantity) || 0);
        this.cartItems = document.getElementById('Cart-Items-Main-' + this.dataset.section);
        this.checkInput = this.querySelector('input[type="checkbox"]');
        if (this.checkInput) {
          this.checkInput.removeEventListener('change', this.boundChange);
          this.checkInput.addEventListener('change', this.boundChange);
        }
      }

      /** 移除包装控件监听，分区重建后不执行旧节点的延迟回调，无参数。 */
      disconnectedCallback() { this.checkInput?.removeEventListener('change', this.boundChange); }

      /**
       * 将包装变更直接交给整车串行队列。
       * @param {Event} event 当前复选框变更。
       */
      onChange(event) {
        if (event.target.checked) {
          this.setWrapping();
        } else {
          this.removeWrapping();
        }
      }

      /**
       * 添加包裹
       */
      setWrapping() {
        const body = JSON.stringify({
          updates: {
            [this.giftWrapId]: this.itemQuantity,
          },
          attributes: {
            "gift-wrap": "Yes",
          },
          sections: this.cartItems
            .getSectionsToRender()
            .map((section) => section.section),
          sections_url: window.location.pathname,
        });

        this.fetchData(body);
      }

      /**
       * 移除包裹
       */
      removeWrapping() {
        const body = JSON.stringify({
          updates: {
            [this.giftWrapId]: 0,
          },
          attributes: {
            "gift-wrap": "",
          },
          sections: this.cartItems
            .getSectionsToRender()
            .map((section) => section.section),
          sections_url: window.location.pathname,
        });

        this.fetchData(body);
      }

      /**
       * 包装写入与数量更新串行，执行前读取实际商品数量。
       * @param {string} body 原生包装更新数据。
       * @returns {Promise<void>} 写入及分区刷新完成。
       */
      fetchData(body) {
        this.enableLoading();
        return CartQuantityQueue.transaction(async () => {
          const payload = JSON.parse(body);
          const cart = await CartQuantityQueue.readCart();
          if (payload.updates[this.giftWrapId] > 0) {
            payload.updates[this.giftWrapId] = cart.items.filter(item => String(item.variant_id) !== String(this.giftWrapId))
              .reduce((sum, item) => sum + item.quantity, 0);
          }
          const sections = CartQuantityQueue.sections();
          payload.sections = [...new Set(sections.map(section => section.section))];
          const result = await CartQuantityQueue.http(window.routes.cart_update_url, payload);
          this.cartItems.handleCartUpdate(result, this.giftWrapId, this.checkInput, sections);
        }).catch(() => { webvista.popToast(window.accessibilityStrings.unknownError, 'error'); })
          .finally(() => this.disableLoading());
      }

      enableLoading() {
        this.cartItems.enableLoading();

        this.checkInput.setAttribute("disabled", "true");
      }

      disableLoading() {
        this.cartItems.disableLoading();

        this.checkInput.removeAttribute("disabled");
      }
    },
  );
}

/**
 * 应用折扣码
 */
if (!customElements.get("cart-code-modal")) {
  customElements.define(
    "cart-code-modal",
    class CartCodeModal extends ModalDialog {
      /** 初始化页面内优惠组件；折扣校验、添加和删除只由 App 承载，无参数。 */
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.submitButton = this.querySelector('button[type=submit]');
        // App 未就绪时也不允许浏览器原生提交表单或按基础价错误应用优惠。
        this.querySelector('form')?.addEventListener('submit', event => {
          event.preventDefault();
          webvista.popToast(window.cartStrings.error, 'error');
        });
      }

      /**
       * 获取需要重新渲染的购物车 Section 配置
       *
       * 根据元素是否具有 `data-in-drawer` 属性，
       * 返回对应的 Section 信息数组。
       *
       * @returns {{id: string, section: string, selector: string}[]}
       */
      getSectionsToRender() {
        if (this.hasAttribute("data-in-drawer")) {
          return [
            {
              id: "Cart-Drawer",
              section: this.sectionId,
              selector: "#Cart-Drawer-Details",
            },
          ];
        } else {
          return [
            {
              id: "Main-Cart",
              section: this.sectionId,
              selector: "#Main-Cart-Details",
            },
          ];
        }
      }

      /**
       * 原生请求仅显示 Loading；App 预检额外占用检查状态，防止数量请求与它交错。
       * @param {boolean} nativeWrite 是否由原生折扣队列调用。
       */
      startLoading(nativeWrite = false) {
        if (!nativeWrite && !this.codeCheckActive) {
          this.codeCheckActive = true;
          CartCheckoutWaitGuard.codeChecks++;
        }
        if (this.submitButton) {
          this.submitButton.classList.add('loading');
          this.submitButton.setAttribute('disabled', 'disabled');
        }
        CartCheckoutWaitGuard.requestSync();
      }

      /** 释放当前弹窗的预检状态，并恢复等待中的数量队列，无参数。 */
      endLoading() {
        if (this.codeCheckActive) {
          this.codeCheckActive = false;
          CartCheckoutWaitGuard.codeChecks--;
        }
        if (this.submitButton) {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('disabled');
        }
        CartQuantityQueue.resume();
        CartCheckoutWaitGuard.requestSync();
      }

    },
  );
}



// 金额报价和草稿结账继续由 App Embed 负责。
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => CartCheckoutWaitGuard.init(), { once: true });
else CartCheckoutWaitGuard.init();
